import { useState, useEffect, useRef } from 'react'
import { useModuleOn } from '../../../shared/hooks/useModuleOn'
import { useAuth } from '../../../hooks/useAuth'
import { Settings, Upload, Save, RefreshCw, AlertCircle, CheckCircle2, Library, Check } from 'lucide-react'
import { bidWorkupService } from '../../../shared/api'
import MachineJobCostCard from '../../finance/components/MachineJobCostCard'
import AwardSignatureSetting from '../components/AwardSignatureSetting'

const DEFAULT_COLOR = '#1f2937'   // neutral slate — never a specific tenant's colour

/**
 * The equipment cost factor as the PERCENTAGE the server will actually use, or '' when it uses none.
 *
 * The server stores what was typed and reads it by one rule (services/calculations.
 * equipment_cost_factor): over 1 is a percent, so it is divided by 100, and the result must fall in
 * (0, 1] or the factor is ignored. So 0.55 and 55 both mean 55%, and a stored 5500 means NOTHING - it
 * is shown blank rather than as a number that would start working the next time the card is saved.
 */
function factorAsPercent(factor) {
  if (factor == null) return ''
  const fraction = factor > 1 ? factor / 100 : factor
  if (!(fraction > 0 && fraction <= 1)) return ''
  return String(Math.round(fraction * 10000) / 100)
}

/**
 * MACHINE COST ON ESTIMATES (PLAN-30 release C). How a capital estimate line that names no machine is
 * costed from Fleet: the most expensive machine of its type, the average, or ask on each line. It ships
 * NOT CHOSEN - nothing is filled in for the company - and "Not chosen yet" is offered only while it is.
 * The one spelling of where it lives is the server's SETTINGS_PATH (src/shared/machine_cost.py):
 * "Admin > Settings, the box 'Machine cost on estimates'" - this card's title is that box.
 */
const MACHINE_COST_RULES = [
  { value: '', label: 'Not chosen yet - a line that names no machine gets no cost from Fleet until someone names a machine or types its cost (the equipment cost factor covers it until then, if one is set)' },
  { value: 'highest', label: 'The most expensive machine of that type' },
  { value: 'average', label: "The average of that type's machines" },
  { value: 'ask', label: 'Ask on each line' },
]

const MACHINE_COST_FOOT =
  'Only machines the company owns, and that are not sold, retired or out of service, are counted. Leased, ' +
  'rented and subcontracted machines can still be named on a line. A machine with no cost in the line\'s unit ' +
  'is left out, and the line says so. The cost is fixed on the line when it is saved; a later change in Fleet ' +
  'shows on the estimate as Machine costs changed and is taken only when someone presses Take. A service bid priced ' +
  'under the current machine rules costs a machine it picks the same way, when it is saved; an older service bid ' +
  'prices its machines from its rate sheet and the equipment list.'

/** A refused save in words: the server's sentence, or each of a 422's reasons, never an object. */
function refusalText(err, fallback) {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail) && detail.length) {
    return detail.map(d => String(d?.msg ?? '').replace(/^Value error, /, '')).filter(Boolean).join(' ') || fallback
  }
  return fallback
}

/** The "When a won bid becomes a project" card's state from a settings response. */
function projectBudgetFrom(data) {
  return {
    basis: data?.project_budget_basis || '',
    factorPct: factorAsPercent(data?.bid_defaults?.equipment_cost_factor),
    factorHidden: Boolean(data?.bid_defaults?.equipment_cost_factor_hidden),
    // Only a factor somebody changed on this card is sent: saving the budget basis must never
    // rewrite a stored factor it did not touch.
    factorTouched: false,
  }
}

function BrandSettings() {
  // Bid defaults and supervision ratios are Bid Workup's own settings (saved through its bid-defaults and
  // supervision-defaults routes, which go with it). With Bid Workup switched off for the company those
  // two cards are left out; the logo, company details and the two company-wide choices stay.
  const bidworkupOn = useModuleOn('bidworkup')
  const { isAdmin } = useAuth()
  const canEdit = isAdmin()

  const [settings, setSettings] = useState(null)
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [savingDefaults, setSavingDefaults] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(null)

  const [form, setForm] = useState({
    company_name: '',
    tagline: '',
    website: '',
    website_url: '',
    brand_color: DEFAULT_COLOR,
  })

  const [bidDefaults, setBidDefaults] = useState({
    // 0, never 12: a shipped consumables percentage was a pre-designed notion (the backend's own
    // default was taken out on 2026-09-12; this page kept a copy of it).
    consumables_pct: 0,
    small_tools_pct: 0,
    scaffold_pct: 0,
    travel_pct: 0,
    onboarding_pct: 0,
  })

  // SHIPS BLANK (C7b, acceptance minor): the company's own roster, empty until it adds a line - never one company's
  // org chart (25/25/25/12.5/12.5/25). A line's trade is PICKED from the rate sheet; its % is blank until typed.
  const [supervisionDefaults, setSupervisionDefaults] = useState([])
  const [classifications, setClassifications] = useState([])
  const [supervisionProblem, setSupervisionProblem] = useState(null)

  // WHEN A WON BID BECOMES A PROJECT (2026-09-27). What its budget is built from - the bid's
  // estimated cost or its selling price - and what a machine with no cost rate costs. Both ship
  // blank: blank means nobody at this company has said, and the software does not say it for them.
  const [projectBudget, setProjectBudget] = useState({ basis: '', factorPct: '', factorHidden: false })
  const [savingProjectBudget, setSavingProjectBudget] = useState(false)
  const [savingSupervision, setSavingSupervision] = useState(false)

  // MACHINE COST ON ESTIMATES: what is stored, and what is chosen on the card (its own Save).
  const [machineRule, setMachineRule] = useState({ stored: '', value: '' })
  const [savingMachineRule, setSavingMachineRule] = useState(false)

  const fileInputRef = useRef(null)

  // Cache-bust the logo preview when it changes
  const [logoCacheBust, setLogoCacheBust] = useState(Date.now())

  // Logo library (S3 picker)
  const [showLibrary, setShowLibrary] = useState(false)
  const [logoLibrary, setLogoLibrary] = useState([])
  const [loadingLibrary, setLoadingLibrary] = useState(false)
  const [selectingLogo, setSelectingLogo] = useState(null)

  useEffect(() => { fetchSettings() }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await bidWorkupService.getSettings()
      setSettings(data)
      setForm({
        company_name: data.company_name || '',
        tagline:      data.tagline      || '',
        website:      data.website      || '',
        website_url:  data.website_url  || '',
        brand_color:  data.brand_color  || DEFAULT_COLOR,
      })
      if (data.bid_defaults) {
        setBidDefaults({
          consumables_pct: data.bid_defaults.consumables_pct ?? 0,
          small_tools_pct: data.bid_defaults.small_tools_pct ?? 0,
          scaffold_pct:    data.bid_defaults.scaffold_pct    ?? 0,
          travel_pct:      data.bid_defaults.travel_pct      ?? 0,
          onboarding_pct:  data.bid_defaults.onboarding_pct  ?? 0,
        })
      }
      setProjectBudget(projectBudgetFrom(data))
      const rule = data.estimate_machine_cost_rule || ''
      setMachineRule({ stored: rule, value: rule })
      if (Array.isArray(data.supervision_defaults)) {
        setSupervisionDefaults(data.supervision_defaults.map(s => ({ ...s })))
      }
      // The trades a line may name: the rate sheet's classifications. A failed read offers none (and says so below).
      Promise.resolve().then(() => bidWorkupService.getClassifications())
        .then((rows) => setClassifications([...new Set((rows || []).map(r => r.classification).filter(Boolean))].sort()))
        .catch(() => setClassifications(null))
    } catch (err) {
      setError('Failed to load settings.')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)
      const data = await bidWorkupService.updateSettings({
        company_name: form.company_name,
        tagline:      form.tagline,
        website:      form.website,
        website_url:  form.website_url,
        brand_color:  form.brand_color,
      })
      setSettings(data)
      setSuccess('Settings saved successfully.')
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save settings.')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveDefaults = async () => {
    try {
      setSavingDefaults(true)
      setError(null)
      setSuccess(null)
      const data = await bidWorkupService.updateBidDefaults({
        consumables_pct: parseFloat(bidDefaults.consumables_pct) || 0,
        small_tools_pct: parseFloat(bidDefaults.small_tools_pct) || 0,
        scaffold_pct:    parseFloat(bidDefaults.scaffold_pct) || 0,
        travel_pct:      parseFloat(bidDefaults.travel_pct) || 0,
        onboarding_pct:  parseFloat(bidDefaults.onboarding_pct) || 0,
      })
      if (data.bid_defaults) setBidDefaults(data.bid_defaults)
      setSuccess('Bid defaults saved successfully.')
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save bid defaults.')
    } finally {
      setSavingDefaults(false)
    }
  }

  const handleSaveProjectBudget = async () => {
    setError(null)
    setSuccess(null)
    const payload = { project_budget_basis: projectBudget.basis }
    // A percentage here, a fraction on the server (services/calculations.equipment_cost_factor):
    // 55 is sent as 0.55, so a typed 0.5 means half a percent rather than half. Blank clears it.
    // Sent only when changed on this card, and only a real percentage: the server reads anything
    // over 1 as a percent, so a typed 150 sent as 1.5 would silently mean 1.5%.
    if (!projectBudget.factorHidden && projectBudget.factorTouched) {
      const pct = String(projectBudget.factorPct).trim()
      const value = parseFloat(pct)
      if (pct !== '' && !(value > 0 && value <= 100)) {
        setError('What a machine with no cost rate costs must be a percentage above 0 and no more than 100, or blank.')
        return
      }
      payload.equipment_cost_factor = pct === '' ? '' : String(value / 100)
    }
    try {
      setSavingProjectBudget(true)
      const data = await bidWorkupService.updateSettings(payload)
      setProjectBudget(projectBudgetFrom(data))
      setSuccess('Project budget settings saved.')
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save the project budget settings.')
    } finally {
      setSavingProjectBudget(false)
    }
  }

  const handleSaveMachineRule = async () => {
    setError(null)
    setSuccess(null)
    try {
      setSavingMachineRule(true)
      // Only this key: the other cards' settings are theirs to save.
      const data = await bidWorkupService.updateSettings({ estimate_machine_cost_rule: machineRule.value })
      const rule = data?.estimate_machine_cost_rule ?? machineRule.value
      setMachineRule({ stored: rule || '', value: rule || '' })
      setSuccess('Machine cost setting saved.')
    } catch (err) {
      setError(refusalText(err, 'Failed to save the machine cost setting.'))
    } finally {
      setSavingMachineRule(false)
    }
  }

  const handleSaveSupervision = async () => {
    // Every line names a trade and states its %: a blank is nobody's choice, so it is not saved as 0.
    const bad = supervisionDefaults.filter(s => !s.classification || String(s.ratio ?? '').trim() === ''
      || !(parseFloat(s.ratio) >= 0 && parseFloat(s.ratio) <= 100))
    if (bad.length) {
      setSupervisionProblem('Each line needs a trade and a % of crew hours from 0 to 100 - or remove the line.')
      return
    }
    setSupervisionProblem(null)
    try {
      setSavingSupervision(true)
      setError(null)
      setSuccess(null)
      const data = await bidWorkupService.updateSupervisionDefaults(
        supervisionDefaults.map(s => ({ classification: s.classification, ratio: parseFloat(s.ratio) }))
      )
      if (Array.isArray(data.supervision_defaults)) setSupervisionDefaults(data.supervision_defaults.map(s => ({ ...s })))
      setSuccess('Supervision defaults saved.')
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save supervision defaults.')
    } finally {
      setSavingSupervision(false)
    }
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setUploading(true)
      setError(null)
      setSuccess(null)
      await bidWorkupService.uploadLogo(file)
      setLogoCacheBust(Date.now())
      // Refresh library so the newly uploaded logo appears
      if (showLibrary) fetchLogoLibrary()
      setSuccess('Logo uploaded and set successfully.')
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to upload logo.')
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const fetchLogoLibrary = async () => {
    try {
      setLoadingLibrary(true)
      const data = await bidWorkupService.listLogos()
      setLogoLibrary(data.logos || [])
    } catch {
      setLogoLibrary([])
    } finally {
      setLoadingLibrary(false)
    }
  }

  const handleToggleLibrary = () => {
    if (!showLibrary && logoLibrary.length === 0) fetchLogoLibrary()
    setShowLibrary(prev => !prev)
  }

  const handleSelectLogo = async (filename) => {
    try {
      setSelectingLogo(filename)
      setError(null)
      setSuccess(null)
      await bidWorkupService.selectLogo(filename)
      setLogoCacheBust(Date.now())
      // Update is_current flag locally
      setLogoLibrary(prev => prev.map(l => ({ ...l, is_current: l.filename === filename })))
      setSettings(prev => prev ? { ...prev, logo_file: filename } : prev)
      setSuccess(`Logo changed to "${filename}".`)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to select logo.')
    } finally {
      setSelectingLogo(null)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <RefreshCw className="h-6 w-6 animate-spin text-gray-400 mr-2" />
      <span className="text-gray-500">Loading settings…</span>
    </div>
  )

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Settings className="h-6 w-6 text-bfs-maroon" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Brand Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure your company&apos;s logo, name, and colors used on exported proposals.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Logo Section */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
        <h2 className="font-semibold text-gray-800">Company Logo</h2>
        <div className="flex items-start space-x-6">
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 flex items-center justify-center w-56 h-24 flex-shrink-0 overflow-hidden">
            <img
              key={logoCacheBust}
              src={`${bidWorkupService.getLogoUrl()}?t=${logoCacheBust}`}
              alt="Current logo"
              className="max-h-full max-w-full object-contain"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Upload a new logo (PNG or JPG, max 10 MB). Use a transparent PNG for best results.<br />
              The logo will appear at the top of every exported proposal.
            </p>
            <p className="text-xs text-gray-400">
              Current file: <code className="bg-gray-100 px-1 rounded">{settings?.logo_file || '—'}</code>
            </p>
            {canEdit && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Upload className="h-4 w-4" />
                  <span>{uploading ? 'Uploading…' : 'Upload New Logo'}</span>
                </button>
                <button
                  onClick={handleToggleLibrary}
                  className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Library className="h-4 w-4" />
                  <span>{showLibrary ? 'Hide Library' : 'Choose from Library'}</span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Logo Library Picker */}
        {showLibrary && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs text-gray-500 mb-3">
              Select an existing logo from storage. The selected logo will be used on all future proposals.
            </p>
            {loadingLibrary ? (
              <div className="flex items-center space-x-2 text-sm text-gray-400">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Loading logos…</span>
              </div>
            ) : logoLibrary.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No logos found in storage.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {logoLibrary.map((logo) => (
                  <div
                    key={logo.filename}
                    className={`relative group border rounded-lg p-2 bg-gray-50 flex flex-col items-center space-y-1 cursor-pointer transition-colors ${
                      logo.is_current
                        ? 'border-bfs-maroon ring-1 ring-bfs-maroon'
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                    onClick={() => canEdit && !logo.is_current && handleSelectLogo(logo.filename)}
                  >
                    <div className="w-full h-14 flex items-center justify-center overflow-hidden">
                      <img
                        src={`${bidWorkupService.getLogoFileUrl(logo.filename)}`}
                        alt={logo.filename}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 truncate w-full text-center leading-tight" title={logo.filename}>
                      {logo.filename}
                    </p>
                    {logo.is_current ? (
                      <span className="flex items-center space-x-0.5 text-xs font-medium text-bfs-maroon">
                        <Check className="h-3 w-3" />
                        <span>Active</span>
                      </span>
                    ) : canEdit && (
                      <button
                        disabled={selectingLogo === logo.filename}
                        className="text-xs px-2 py-0.5 rounded bg-bfs-maroon text-white hover:bg-bfs-maroon/90 disabled:opacity-50"
                        onClick={(e) => { e.stopPropagation(); handleSelectLogo(logo.filename) }}
                      >
                        {selectingLogo === logo.filename ? '…' : 'Use'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Company Info + Color */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5">
        <h2 className="font-semibold text-gray-800">Company Information</h2>

        <Field label="Company Name" id="company_name">
          <input
            id="company_name"
            type="text"
            value={form.company_name}
            onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            disabled={!canEdit}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
          />
        </Field>

        <Field label="Tagline" id="tagline">
          <input
            id="tagline"
            type="text"
            value={form.tagline}
            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
            disabled={!canEdit}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
          />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Website (display)" id="website">
            <input
              id="website"
              type="text"
              placeholder="www.example.com"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
            />
          </Field>
          <Field label="Website URL (link)" id="website_url">
            <input
              id="website_url"
              type="url"
              placeholder="https://www.example.com"
              value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
            />
          </Field>
        </div>

        <Field label="Brand Color" id="brand_color">
          <div className="flex items-center space-x-3">
            <input
              id="brand_color"
              type="color"
              value={form.brand_color}
              onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
              disabled={!canEdit}
              className="h-10 w-20 border border-gray-300 rounded-lg cursor-pointer disabled:opacity-50"
            />
            <input
              type="text"
              value={form.brand_color}
              onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
              disabled={!canEdit}
              maxLength={7}
              className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
              placeholder="#1f2937"
            />
            <span className="text-xs text-gray-400">Used for headings and signature labels in proposals</span>
          </div>
        </Field>

        {canEdit && (
          <div className="pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-5 py-2 bg-bfs-maroon text-white rounded-lg text-sm font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{saving ? 'Saving…' : 'Save Changes'}</span>
            </button>
          </div>
        )}

        {!canEdit && (
          <p className="text-xs text-gray-400">Only administrators can edit brand settings.</p>
        )}
      </div>

      {/* Bid Calculation Defaults */}
      {bidworkupOn && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">Bid Calculation Defaults</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              These percentages are pre-loaded into every new bid. Each bid can still be adjusted individually.
            </p>
          </div>
  
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { key: 'consumables_pct',  label: 'Consumables %',  hint: '% of labor + supervision + per diem' },
              { key: 'small_tools_pct',  label: 'Small Tools %',  hint: '% of labor' },
              { key: 'scaffold_pct',     label: 'Scaffold %',     hint: '% of labor' },
              { key: 'travel_pct',       label: 'Travel %',       hint: '% of labor' },
              { key: 'onboarding_pct',   label: 'On Boarding %',  hint: '% of labor' },
            ].map(({ key, label, hint }) => (
              <div key={key} className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={bidDefaults[key]}
                    onChange={e => setBidDefaults(p => ({ ...p, [key]: e.target.value }))}
                    disabled={!canEdit}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                </div>
                <p className="text-xs text-gray-400">{hint}</p>
              </div>
            ))}
          </div>
  
          {canEdit && (
            <div className="pt-2">
              <button
                onClick={handleSaveDefaults}
                disabled={savingDefaults}
                className="flex items-center space-x-2 px-5 py-2 bg-bfs-maroon text-white rounded-lg text-sm font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                <span>{savingDefaults ? 'Saving…' : 'Save Bid Defaults'}</span>
              </button>
            </div>
          )}
          {!canEdit && (
            <p className="text-xs text-gray-400">Only administrators can edit bid defaults.</p>
          )}
        </div>
      )}

      {/* When a won bid becomes a project */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5">
        <div>
          <h2 className="font-semibold text-gray-800">When a won bid becomes a project</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Converting a won opportunity builds the new project&apos;s budget from its bid. Choose what that
            budget is. Until one is chosen, a converted project is created without a budget, and the
            Convert screen says so.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="project_budget_basis" className="block text-sm font-medium text-gray-700">Project budget is built from</label>
            <select
              id="project_budget_basis"
              value={projectBudget.basis}
              onChange={e => setProjectBudget(p => ({ ...p, basis: e.target.value }))}
              disabled={!canEdit}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">Not chosen</option>
              <option value="estimated_cost">The bid&apos;s estimated cost</option>
              <option value="selling_price">The bid&apos;s selling price</option>
            </select>
            <p className="text-xs text-gray-400">
              Estimated cost: labour at wage plus burden, equipment at its cost rate, materials at unit
              cost. Selling price: what the bid charges, fee included.
            </p>
          </div>

          <div className="space-y-1">
            <label htmlFor="equipment_cost_factor" className="block text-sm font-medium text-gray-700">Machines with no cost rate cost</label>
            {projectBudget.factorHidden ? (
              <div className="w-full bg-gray-100 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-500">Hidden</div>
            ) : (
              <div className="relative">
                <input
                  id="equipment_cost_factor"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Not set"
                  value={projectBudget.factorPct}
                  onChange={e => setProjectBudget(p => ({ ...p, factorPct: e.target.value, factorTouched: true }))}
                  disabled={!canEdit}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-24 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">% of billing</span>
              </div>
            )}
            <p className="text-xs text-gray-400">
              Unless a bid sets its own factor, this is used for a bid machine line&apos;s estimated cost when the line
              has no cost rate - in the bid&apos;s margin and in a cost budget. Left blank, such a machine is reported
              as uncosted rather than given a cost. A capital estimate takes a machine&apos;s cost from Fleet (the box
              Machine cost on estimates, below), and so does a service bid priced under the current machine rules for a machine
              it picks; an older service bid takes its machines&apos; cost from its rate sheet and the equipment list.
            </p>
          </div>
        </div>

        {canEdit && (
          <div className="pt-2">
            <button
              onClick={handleSaveProjectBudget}
              disabled={savingProjectBudget}
              className="flex items-center space-x-2 px-5 py-2 bg-bfs-maroon text-white rounded-lg text-sm font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{savingProjectBudget ? 'Saving…' : 'Save Project Budget Settings'}</span>
            </button>
          </div>
        )}
        {!canEdit && (
          <p className="text-xs text-gray-400">Only administrators can change these.</p>
        )}
      </div>

      {/* Machine cost on estimates (PLAN-30 release C) */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5" data-testid="machine-cost-rule-card">
        <div>
          <h2 className="font-semibold text-gray-800">Machine cost on estimates</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            An estimate line prices a machine type. Its cost comes from a machine in Fleet. When a line names no
            machine, cost it at:
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="sr-only">When a line names no machine, cost it at</legend>
          {MACHINE_COST_RULES.filter(o => o.value !== '' || machineRule.stored === '').map(o => (
            <label key={o.value || 'blank'} className="flex items-start space-x-2 text-sm text-gray-700">
              <input
                type="radio"
                name="estimate_machine_cost_rule"
                value={o.value}
                checked={machineRule.value === o.value}
                onChange={() => setMachineRule(r => ({ ...r, value: o.value }))}
                disabled={!canEdit}
                className="mt-0.5"
              />
              <span>{o.label}</span>
            </label>
          ))}
        </fieldset>
        <p className="text-xs text-gray-400">{MACHINE_COST_FOOT}</p>
        {/* 2.104.0: this card is what an ESTIMATE costs a machine at. What a daily report CHARGES for one is
            a different pair of choices, kept beside the rates they govern - said here so nobody looks for
            them on this page. */}
        <p className="text-xs text-gray-500" data-testid="machine-billing-pointer">
          How machines bill from daily reports: Fleet &gt; Machine Types
        </p>

        {canEdit && (
          <div className="pt-2">
            <button
              onClick={handleSaveMachineRule}
              disabled={savingMachineRule || machineRule.value === machineRule.stored}
              className="flex items-center space-x-2 px-5 py-2 bg-bfs-maroon text-white rounded-lg text-sm font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              <span>{savingMachineRule ? 'Saving…' : 'Save Machine Cost Setting'}</span>
            </button>
          </div>
        )}
        {!canEdit && (
          <p className="text-xs text-gray-400">Only administrators can change this.</p>
        )}
      </div>

      {/* Job cost for machines comes from (PLAN-30 P4) - ships blank; dated; re-posts nothing by itself */}
      <MachineJobCostCard canEdit={canEdit} />

      {/* An award needs the signed proposal (Capex completion C2, item 8) - ships blank = not required */}
      {bidworkupOn && <AwardSignatureSetting canEdit={canEdit} />}

      {/* Supervision Defaults */}
      {bidworkupOn && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-5">
          <div>
            <h2 className="font-semibold text-gray-800">Default Supervision Ratios</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Supervision hours are calculated as a percentage of total crew hours. These defaults
              are applied to every new bid and can still be adjusted per-bid.
            </p>
          </div>
  
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
                  <th className="pb-2 pr-4">Classification</th>
                  <th className="pb-2 w-36">% of Crew Hrs</th><th className="pb-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {supervisionDefaults.length === 0 && (
                  <tr><td colSpan={3} className="py-3 text-gray-500" data-testid="supervision-none">
                    None - a new bid starts with no supervision, and the estimator adds what the job has. Add a line to give every new bid one.
                  </td></tr>
                )}
                {supervisionDefaults.map((row, idx) => (
                  <tr key={idx} data-testid="supervision-default-row">
                    <td className="py-2 pr-4 text-gray-700">
                      {row.picked ? (
                        <select aria-label={`Trade of line ${idx + 1}`} value={row.classification} disabled={!canEdit}
                          onChange={e => setSupervisionDefaults(prev => prev.map((s, i) => i === idx ? { ...s, classification: e.target.value } : s))}
                          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                          <option value="">Pick a trade…</option>
                          {(classifications || []).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : row.classification}
                    </td>
                    <td className="py-2">
                      <div className="relative w-32">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          aria-label={`% of crew hours, line ${idx + 1}`}
                          value={row.ratio ?? ''}
                          onChange={e => setSupervisionDefaults(prev => prev.map((s, i) => i === idx ? { ...s, ratio: e.target.value } : s))}
                          disabled={!canEdit}
                          className="w-full border border-gray-300 rounded-lg px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-bfs-maroon disabled:bg-gray-50 disabled:text-gray-400"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                      </div>
                    </td>
                    <td className="py-2 pl-2">
                      {canEdit && (
                        <button type="button" className="text-sm text-gray-500 hover:text-red-700"
                          aria-label={`Remove line ${idx + 1}`}
                          onClick={() => setSupervisionDefaults(prev => prev.filter((_s, i) => i !== idx))}>Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
  
          {canEdit && (
            <button type="button" className="text-sm text-bfs-maroon hover:underline"
              onClick={() => setSupervisionDefaults(prev => [...prev, { classification: '', ratio: '', picked: true }])}>
              + Add a line
            </button>
          )}
          {classifications === null && (
            <p className="text-xs text-amber-700">The rate sheet&apos;s trades could not be read just now, so a new line has none to pick.</p>
          )}
          {supervisionProblem && <p className="text-sm text-red-700" role="alert">{supervisionProblem}</p>}
          {canEdit && (
            <div className="pt-2">
              <button
                onClick={handleSaveSupervision}
                disabled={savingSupervision}
                className="flex items-center space-x-2 px-5 py-2 bg-bfs-maroon text-white rounded-lg text-sm font-semibold hover:bg-bfs-maroon/90 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                <span>{savingSupervision ? 'Saving…' : 'Save Supervision Defaults'}</span>
              </button>
            </div>
          )}
          {!canEdit && (
            <p className="text-xs text-gray-400">Only administrators can edit supervision defaults.</p>
          )}
        </div>
      )}

      <p className="text-xs text-gray-400 text-center">
        Changes take effect immediately on the next proposal export — no restart required.
      </p>
    </div>
  )
}

function Field({ label, id, children }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  )
}

export default BrandSettings
